import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { DocumentTextIcon } from '@heroicons/react/24/outline';
import type { ChatQuelle } from '../../api/client';

/**
 * Antworttext (Markdown) mit Belegen: „[1]" wird zu einem Knopf, der die Quelle rechts öffnet. Unbekannte Nummern
 * (vom Modell erfunden) bleiben schlichter Text. Darunter die Quellenliste — live geprüft vom Backend.
 */

type Props = { text: string; quellen: ChatQuelle[]; onQuelle: (q: ChatQuelle) => void; aktiv?: string | null };

export function Quellenliste({ quellen, onQuelle, aktiv }: Omit<Props, 'text'>) {
  if (!quellen.length) return null;
  return (
    <div className="mt-3 border-t border-gray-100 pt-2">
      <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-ps-dark-400">Quellen</div>
      <ul className="space-y-1">
        {quellen.map((q) => (
          <li key={q.n}>
            {q.verfuegbar ? (
              <button type="button" onClick={() => onQuelle(q)}
                className={`flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left text-[13px] hover:bg-primary-50 ${aktiv === q.datei_id ? 'bg-primary-50 ring-1 ring-primary-200' : ''}`}>
                <span className="numerical mt-px inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded bg-primary-100 px-1 text-[11px] font-semibold text-primary-800">{q.n}</span>
                <DocumentTextIcon className="mt-0.5 h-4 w-4 shrink-0 text-primary-500" />
                <span className="min-w-0 flex-1">
                  <span className="break-words font-medium text-ps-dark-800">{q.name}</span>
                  {q.seite != null && <span className="text-ps-dark-400"> · Seite {q.seite}</span>}
                  <span className="block break-all text-[11px] text-ps-dark-400">{q.pfad}</span>
                  {q.geaendert_seither && <span className="block text-[11px] text-amber-700">Datei wurde seit der Antwort geändert</span>}
                </span>
              </button>
            ) : (
              <div className="flex items-center gap-2 px-2 py-1.5 text-[13px] text-ps-dark-400">
                <span className="numerical inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded bg-gray-100 px-1 text-[11px] font-semibold">{q.n}</span>
                Quelle nicht mehr verfügbar
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Antwort({ text, quellen, onQuelle, aktiv }: Props) {
  const nachNr = new Map(quellen.map((q) => [q.n, q]));
  // [n] → Markdown-Link auf #quelle-n; der Link-Renderer macht daraus einen Knopf
  const md = text.replace(/\[(\d{1,3})\](?!\()/g, (ganz, n) => (nachNr.has(Number(n)) ? `[${n}](#quelle-${n})` : ganz));
  return (
    <div>
      <div className="antwort prose-sm max-w-none text-[14.5px] leading-relaxed text-ps-dark-800">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
          a: ({ href, children }) => {
            const m = /^#quelle-(\d+)$/.exec(href ?? '');
            const q = m ? nachNr.get(Number(m[1])) : undefined;
            if (q) {
              return (
                <button type="button" onClick={() => q.verfuegbar && onQuelle(q)} title={q.verfuegbar ? q.name : 'Quelle nicht mehr verfügbar'}
                  className={`numerical mx-0.5 inline-flex h-[18px] min-w-[18px] -translate-y-px items-center justify-center rounded px-1 align-middle text-[11px] font-semibold ${q.verfuegbar ? 'bg-primary-100 text-primary-800 hover:bg-primary-200' : 'bg-gray-100 text-ps-dark-400'}`}>
                  {children}
                </button>
              );
            }
            return <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary-700 underline">{children}</a>;
          },
        }}>{md}</ReactMarkdown>
      </div>
      <Quellenliste quellen={quellen} onQuelle={onQuelle} aktiv={aktiv} />
    </div>
  );
}
